/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { environment, ModulesEnum } from '../../../../../environments/environment';

@Injectable()
export class DrawerService {
    /**
     * Content emitter for setting drawer content when navigating between pages
     */
    public contentEmitter: Subject<any> = new Subject<any>();

    /**
     * Emit drawer state
     */
    public drawerEmitter: Subject<boolean> = new Subject<boolean>();

    /**
     * Emit drawer state
     */
    public loadingEmitter: Subject<boolean> = new Subject<boolean>();

    /**
     * Drawer content store
     */
    private _drawerContent: any = {};

    /**
     * Drawer state store
     */
    private _drawerState = {
        drawContent: {
            title: '',
            actionDots: '',
            content: '',
            template: '',
            data: { id: '' }
        },
        visibility: false,
        styles: {
            width: 0,
            transitionTiming: 0
        }
    };

    /**
     * Stores open width for use in animations
     */
    private openWidth: number = 0;

    /**
     * Store trigger clicks
     * @private
     */
    private _storedTriggerClick: string = null;

    constructor(
        private router: Router
    ) {
        this.setupNavigationSubscribers();
    }

    get drawerState(): any {
        return this._drawerState;
    }

    set drawerState(state) {
        this._drawerState = state;
    }

    get drawerContent(): any {
        return this._drawerContent;
    }

    set drawerContent(content: any) {
        this._drawerContent = content;
    }

    get storedTriggerClick() {
        return this._storedTriggerClick;
    }

    set storedTriggerClick(state: any) {
        this._storedTriggerClick = state;
    }

    /**
     * Get the drawer stored content
     * @param drawerId
     */
    public getStoredDrawerContent(drawerId: string): string {
        return this.drawerContent[drawerId]?.drawerHtml;
    }

    /**
     * Store the drawer content.
     * @param drawerHtml
     * @param drawerId
     */
    public storeDrawerContent(drawerHtml: string, drawerId: string): void {
        this.drawerContent = { [drawerId]: { drawerHtml } };
    }

    /**
     * Returns drawer visibility state
     */
    public getDrawerVisibility(): boolean {
        return this._drawerState.visibility;
    }

    /**
     * Open Drawer if not in the DOM and set the content
     * @param { title, content, template }
     */
    public open({ title, actionDots, content, template, data }: any): void {
        this._drawerState.drawContent = {
            title,
            actionDots,
            content,
            template,
            data
        };
        this._drawerState.visibility = true;
        this._drawerState.styles.width = this.openWidth;
        this.contentEmitter.next(this._drawerState);
        this.drawerEmitter.next(true);
        this.loadingEmitter.next(false);
        this.setLocalStorage();
    }

    /**
     * Set drawer values to closed.
     */
    public close(): void {
        this._drawerState.visibility = false;
        this._drawerState.styles.width = 0;
        this.contentEmitter.next(this._drawerState);
        this.drawerEmitter.next(false);
        this.setLocalStorageVisibilityOnClose();
    }

    /**
     * Sets the open width of the drawer and stores it in the drawer state.
     * @param width
     */
    public setWidth(width: number): void {
        this.openWidth = width;
        this._drawerState.styles.width = width;
    }

    /**
     * Sets transition timings for animation in drawer state.
     * @param timing
     */
    public setTransitionTimings(timing: number): void {
        this._drawerState.styles.transitionTiming = timing;
    }

    /**
     * Set up navigation subscriber for closing the drawer.
     * @private
     */
    private setupNavigationSubscribers(): void {
        this.router.events
            .subscribe({
                next: (event: any): void => {
                    if (event instanceof NavigationStart) {
                        if (environment.module === ModulesEnum.LOADBALANCER) {
                            this.close();
                        } else {
                            this.loadingEmitter.next(true);
                        }
                    }

                    if (event instanceof NavigationEnd) {
                        const drawerStorage = JSON.parse(localStorage.getItem('drawerState')) || {};
                        if (drawerStorage && !drawerStorage[event.url]?.visibility) {
                            this.close();
                        }
                    }
                }
            });
    }

    /**
     * Set local storage.
     * @private
     */
    private setLocalStorage(): void {
        const drawerStorage = JSON.parse(localStorage.getItem('drawerState')) || {};
        drawerStorage[location.pathname.toString()] = {
            id: this._drawerState.drawContent?.data?.id || this._drawerState.drawContent.title,
            visibility: this._drawerState.visibility,
        };
        localStorage.setItem('drawerState', JSON.stringify(drawerStorage));
    }

    /**
     * Update local storage visibility on close.
     * @private
     */
    private setLocalStorageVisibilityOnClose(): void {
        const drawerStorage = JSON.parse(localStorage.getItem('drawerState')) || {};
        const path = location.pathname.toString();
        if (drawerStorage[path]) {
            delete drawerStorage[path];
            localStorage.setItem('drawerState', JSON.stringify(drawerStorage));
        }
    }
}
